<script>

    import { onDestroy, onMount } from 'svelte'
    import { replace } from 'svelte-spa-router'
    import Moment from 'moment'
    import { toast } from 'bulma-toast'

    import View from '../$layouts/view.layout.svelte'
    import PageHeader from '../$components/page.header.svelte'
    import DateDayNav from '../$components/date.day.nav.svelte'
    import DriversService from '../$services/drivers.service'
    import NumeralMethods from '../$methods/numeral.methods'
    import DateMethod from '../$methods/date.method'
    import Map from './component.map.svelte'
    import Delivery from './component.delivery.svelte'
    import { dayFromHash } from './day'
    import { remainingMeters, remainingSeconds } from './route.km'

    const REFRESH_MS = 15000

    let day = dayFromHash()
    let drivers = []
    let selectedId = ''
    let detail = null
    let loading = false
    let detailLoading = false
    let listRequest = 0
    let detailRequest = 0
    let timer
    let mapWrap
    let focus = null
    let focusSeq = 0
    let deliveryId = null

    $: if (day)
        load(day, false)

    onMount(() => {
        timer = setInterval(() => {
            if (!day) return
            load(day, true)
            if (selectedId) loadDetail(selectedId, day, true)
        }, REFRESH_MS)
    })

    onDestroy(() => {
        if (timer) clearInterval(timer)
    })

    function setDay(value) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '') || value === day) return
        day = value
        selectedId = ''
        detail = null
        focus = null
        replace(`#/til-seguimiento?day=${day}`)
    }

    function select(id) {
        const next = String(id || '')
        focus = null

        if (!next || selectedId === next) {
            selectedId = ''
            detail = null
            return
        }

        selectedId = next
        detail = null
        loadDetail(next, day, false)
    }

    async function load(selected, silent) {
        const id = ++listRequest
        if (!silent) loading = true

        const resp = await DriversService.getTracking({ day: selected })

        if (id !== listRequest) return

        loading = false

        if (!resp.success) {
            if (!silent || !drivers.length)
                toast({ message: (resp.error && resp.error.message) || 'No se pudo consultar el seguimiento', type: 'is-danger' })
            return
        }

        drivers = resp.data.drivers || []

        if (selectedId && !drivers.some(driver => String(driver.driverId) === selectedId)) {
            selectedId = ''
            detail = null
        }
    }

    async function loadDetail(id, selected, silent) {
        const token = ++detailRequest
        if (!silent) detailLoading = true

        const resp = await DriversService.getDriverTracking(id, { day: selected })

        if (token !== detailRequest || selectedId !== String(id)) return

        detailLoading = false

        if (!resp.success) {
            if (!silent)
                toast({ message: (resp.error && resp.error.message) || 'No se pudo consultar el driver', type: 'is-danger' })
            return
        }

        detail = resp.data
    }

    $: pointByShipment = pointIndex(detail && detail.route)
    $: orderedPackages = sortByPoint(detail && detail.packages, pointByShipment)
    $: statuses = statusBreakdown(detail && detail.packages)
    $: kilometers = formatKm(detail && detail.route && detail.route.totals && detail.route.totals.distanceMeters)
    $: remaining = formatKm(remainingMeters(detail && detail.route, driverPosition(selectedDriver, detail)))
    $: finish = finishAt(detail && detail.route, driverPosition(selectedDriver, detail))
    $: selectedDriver = drivers.find(driver => String(driver.driverId) === String(selectedId))
    $: deliveredIds = deliveredByShipment(detail && detail.packages)

    function pointIndex(route) {
        const index = {}

        for (const stop of (route && route.stops) || []) {
            if (stop.order == null) continue
            const position = stop.position || {}
            const point = {
                order: stop.order,
                lat: position.lat,
                lng: position.lng,
            }
            for (const shipmentId of stop.shipmentIds || [])
                index[String(shipmentId)] = point
        }

        return index
    }

    function sortByPoint(items, index) {
        const byId = index || {}

        return (items || [])
            .map((item, position) => ({
                item,
                position,
                order: (byId[String((item && item.shipmentId) || '')] || {}).order,
            }))
            .sort((a, b) => {
                if (a.order == null && b.order == null) {
                    const aAt = deliveredAt(a.item)
                    const bAt = deliveredAt(b.item)
                    if (aAt != null && bAt != null) return aAt - bAt
                    if (aAt != null) return -1
                    if (bAt != null) return 1
                    return a.position - b.position
                }
                if (a.order == null) return 1
                if (b.order == null) return -1
                return a.order - b.order || a.position - b.position
            })
            .map(entry => entry.item)
    }

    function deliveredAt(item) {
        const scans = [item && item.last, item && item.previous]
        let at = null

        for (const scan of scans) {
            if (!scan || !scan.name || !scan.name.includes('Entregado') || !scan.created) continue
            const ms = new Date(scan.created).getTime()
            if (Number.isNaN(ms)) continue
            if (at == null || ms > at) at = ms
        }

        return at
    }

    function changedPair(item) {
        return !!(item && item.previous && item.last && item.previous.name !== item.last.name)
    }

    function scanAt(item) {
        const scan = item && item.last
        if (!scan || !scan.created) return null
        const ms = new Date(scan.created).getTime()
        return Number.isNaN(ms) ? null : ms
    }

    function formatMinutes(ms) {
        const minutes = Math.round(ms / 60000)
        if (Number.isNaN(minutes) || minutes < 0) return ''

        const hours = Math.floor(minutes / 60)
        const rest = minutes % 60
        if (hours && rest) return `${hours} h ${rest} min`
        if (hours) return `${hours} h`
        return `${rest} min`
    }

    function timeSincePreviousPoint(item) {
        const order = pointNumber(item)
        const previous = order === 1 ? null : (order ? previousStopDeliveredAt(order) : previousOffRouteDeliveredAt(item))
        const current = deliveredAt(item) != null ? deliveredAt(item) : scanAt(item)

        if (previous != null && current != null)
            return formatMinutes(current - previous)

        return timeBeforeDelivered(item)
    }

    function timeBeforeDelivered(item) {
        const delivered = item && item.last
        const before = item && item.previous
        if (!delivered || !before || !delivered.created || !before.created) return ''
        if (!String(delivered.name || '').includes('Entregado')) return ''
        return formatMinutes(new Date(delivered.created) - new Date(before.created))
    }

    function previousStopDeliveredAt(order) {
        let previous = null

        for (const entry of orderedPackages || []) {
            const stop = pointNumber(entry)
            if (stop === '' || stop >= order) continue
            if (previous == null || stop > previous) previous = stop
        }

        return previous == null ? null : stopDeliveredAt(previous)
    }

    function stopDeliveredAt(order) {
        let at = null

        for (const entry of orderedPackages || []) {
            if (pointNumber(entry) !== order) continue
            const ms = deliveredAt(entry)
            if (ms == null) continue
            if (at == null || ms < at) at = ms
        }

        return at
    }

    function previousOffRouteDeliveredAt(item) {
        const list = orderedPackages || []
        const index = list.indexOf(item)
        if (index <= 0) return null

        const previous = list[index - 1]
        const stop = pointNumber(previous)
        if (stop === '') return deliveredAt(previous)
        return stopDeliveredAt(stop)
    }

    function pointNumber(item) {
        const point = pointByShipment[String((item && item.shipmentId) || '')]
        return point && point.order != null ? point.order : ''
    }

    function statusBreakdown(items) {
        const counts = {}

        for (const item of items || []) {
            const name = item.name || 'Sin estatus'
            counts[name] = (counts[name] || 0) + 1
        }

        return Object.keys(counts)
            .map(name => ({ name, packages: counts[name] }))
            .sort((a, b) => b.packages - a.packages || a.name.localeCompare(b.name, 'es'))
    }

    function formatKm(meters) {
        if (meters == null || Number.isNaN(meters)) return ''
        return (meters / 1000).toLocaleString('es-MX', { maximumFractionDigits: 1 })
    }

    function driverPosition(driver, value) {
        const listed = driver && driver.position
        const loaded = value && value.driver && value.driver.position
        const position = listed && listed.lat != null ? listed : loaded
        if (!position || position.lat == null || position.lng == null) return null
        return position
    }

    function finishAt(route, position) {
        const seconds = remainingSeconds(route, position)
        if (seconds == null) return ''
        return DateMethod.central(Moment().add(seconds, 'seconds')).format('h:mm a')
    }

    function isDelivered(item) {
        const name = (item && item.name) || ''
        return name.includes('Entregado')
    }

    function deliveredByShipment(items) {
        const ids = {}
        for (const item of items || []) {
            if (isDelivered(item)) ids[String(item.shipmentId)] = true
        }
        return ids
    }

    function guidePoint(item) {
        const point = pointByShipment[String((item && item.shipmentId) || '')]
        if (!point || point.lat == null || point.lng == null) return null
        return point
    }

    function focusGuide(item) {
        const point = guidePoint(item)
        if (!point) return

        focus = { lat: point.lat, lng: point.lng }
        focusSeq += 1
        if (mapWrap)
            mapWrap.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }

</script>

<style>
    .toolbar {
        display: flex;
        justify-content: flex-end;
        margin-bottom: 1rem;
    }

    .cards {
        display: flex;
        gap: 0.75rem;
        overflow-x: auto;
        padding-bottom: 0.35rem;
        margin-bottom: 1.25rem;
    }

    .card {
        flex: 0 0 200px;
        text-align: left;
        background: #fff;
        border: 2px solid transparent;
        border-radius: 18px;
        box-shadow: 0 8px 28px rgba(0, 0, 0, 0.06);
        padding: 0.9rem 1rem;
        cursor: pointer;
    }

    .card.is-selected {
        border-color: var(--color-primary, #098E37);
    }

    .card__name {
        margin: 0;
        font-size: 14px;
        font-weight: 700;
        color: #1a1a1a;
    }

    .card__meta {
        margin: 0.2rem 0 0;
        font-size: 12px;
        color: #888;
    }

    .card__count {
        margin: 0.55rem 0 0;
        font-size: 18px;
        font-weight: 700;
        color: var(--color-primary, #098E37);
    }

    .empty {
        margin: 0 0 1.25rem;
        color: #888;
        font-size: 14px;
    }

    .map-row {
        display: flex;
        align-items: stretch;
        gap: 1rem;
        margin-bottom: 1.25rem;
    }

    .map-wrap {
        flex: 1;
        min-width: 0;
    }

    .side {
        flex: 0 0 280px;
        display: flex;
        flex-direction: column;
        gap: 1rem;
        height: 560px;
        overflow: auto;
        background: #fff;
        border-radius: 28px;
        box-shadow: 0 8px 28px rgba(0, 0, 0, 0.06);
        padding: 1.25rem;
    }

    .side h2 {
        margin: 0;
        font-size: 16px;
        font-weight: 700;
    }

    .side__label {
        margin: 0 0 0.25rem;
        color: #888;
        font-size: 12px;
        font-weight: 600;
    }

    .side__value {
        margin: 0;
        font-size: 22px;
        font-weight: 700;
        color: #1a1a1a;
    }

    .side__statuses {
        margin: 0;
        padding: 0;
        list-style: none;
    }

    .side__statuses li {
        display: flex;
        justify-content: space-between;
        gap: 0.75rem;
        padding: 0.35rem 0;
        border-bottom: 1px solid #f0f0f0;
        font-size: 13px;
        color: #3a3a3a;
    }

    .side__statuses strong {
        color: var(--color-primary, #098E37);
    }

    @media (max-width: 900px) {
        .map-row {
            flex-direction: column;
        }

        .side {
            flex: none;
            height: auto;
            max-height: 420px;
        }
    }

    .detail {
        background: #fff;
        border-radius: 28px;
        box-shadow: 0 8px 28px rgba(0, 0, 0, 0.06);
        padding: 1.25rem 1.5rem;
        margin-bottom: 1.25rem;
    }

    .detail h2 {
        margin: 0 0 0.75rem;
        font-size: 16px;
        font-weight: 700;
    }

    .packages {
        width: 100%;
        font-size: 13px;
    }

    .packages th {
        font-size: 12px;
        color: #888;
        font-weight: 600;
    }

    .packages .point {
        width: 4.5rem;
        font-weight: 700;
        color: #1a1a1a;
    }

    .guide {
        background: none;
        border: none;
        padding: 0;
        color: var(--color-primary, #098E37);
        font: inherit;
        font-weight: 700;
        cursor: pointer;
        text-decoration: underline;
    }

    .scan {
        display: flex;
        flex-direction: column;
        gap: 0.15rem;
    }

    .scan__time {
        color: #888;
        font-size: 12px;
    }
</style>

<View>
    <PageHeader title="Seguimiento" subtitle="Ubicación de los drivers y paquetes del día (hora centro de México)" />

    <div class="toolbar">
        <DateDayNav {day} on:change={(event) => setDay(event.detail)} />
    </div>

    {#if drivers.length}
        <div class="cards">
            {#each drivers as driver}
                <button
                    type="button"
                    class="card"
                    class:is-selected={String(driver.driverId) === selectedId}
                    on:click={() => select(driver.driverId)}
                >
                    <p class="card__name">{ driver.name || 'Sin nombre' }</p>
                    <p class="card__meta">{ driver.email || '' }</p>
                    <p class="card__count">{ NumeralMethods.number(driver.packages) } paquetes</p>
                </button>
            {/each}
        </div>
    {:else if !loading}
        <p class="empty">Ningún driver escaneó paquetes ni operó este día.</p>
    {/if}

    <div class="map-row">
        <div class="map-wrap" bind:this={mapWrap}>
            <Map
                {drivers}
                {day}
                {selectedId}
                {focus}
                {focusSeq}
                route={detail && detail.route}
                delivered={deliveredIds}
                routeNote={!!(selectedId && detail && !detail.route)}
                on:select={(event) => select(event.detail)}
            />
        </div>

        {#if selectedId}
            <aside class="side">
                <h2>{ (detail && detail.driver && detail.driver.name) || (selectedDriver && selectedDriver.name) || 'Driver' }</h2>
                {#if detailLoading && !detail}
                    <p class="empty">Cargando…</p>
                {:else if detail}
                    <div>
                        <p class="side__label">Paquetes</p>
                        <p class="side__value">{ NumeralMethods.number(detail.packages ? detail.packages.length : 0) }</p>
                    </div>
                    <div>
                        <p class="side__label">Recorrido</p>
                        <p class="side__value">{ kilometers !== '' ? `${kilometers} km` : '—' }</p>
                    </div>
                    <div>
                        <p class="side__label">Restantes</p>
                        <p class="side__value">{ remaining !== '' ? `${remaining} km` : '—' }</p>
                    </div>
                    <div>
                        <p class="side__label">Finalización aproximada</p>
                        <p class="side__value">{ finish || '—' }</p>
                    </div>
                    <div>
                        <p class="side__label">Estatus</p>
                        {#if statuses.length}
                            <ul class="side__statuses">
                                {#each statuses as status}
                                    <li>
                                        <span>{ status.name }</span>
                                        <strong>{ NumeralMethods.number(status.packages) }</strong>
                                    </li>
                                {/each}
                            </ul>
                        {:else}
                            <p class="empty">Sin escaneos</p>
                        {/if}
                    </div>
                {/if}
            </aside>
        {/if}
    </div>

    {#if selectedId}
        <section class="detail">
            <h2>{ (detail && detail.driver && detail.driver.name) || 'Driver' }</h2>
            {#if detailLoading && !detail}
                <p class="empty">Cargando paquetes…</p>
            {:else if detail}
                {#if detail.packages && detail.packages.length}
                    <table class="table is-fullwidth packages">
                        <thead>
                            <tr>
                                <th class="point">Punto</th>
                                <th>Guía</th>
                                <th>Anterior</th>
                                <th>Último</th>
                                <th>Tiempo</th>
                                <th>Entrega</th>
                            </tr>
                        </thead>
                        <tbody>
                            {#each orderedPackages as item}
                                <tr>
                                    <td class="point">{ pointNumber(item) || '—' }</td>
                                    <td>
                                        {#if item.guide && guidePoint(item)}
                                            <button type="button" class="guide" on:click={() => focusGuide(item)}>{ item.guide }</button>
                                        {:else}
                                            { item.guide || '—' }
                                        {/if}
                                    </td>
                                    <td>
                                        {#if changedPair(item)}
                                            <span class="scan">
                                                <span>{ item.previous.name }</span>
                                                <span class="scan__time">{ item.previous.created ? DateMethod.centralHourLarge(item.previous.created) : '—' }</span>
                                            </span>
                                        {:else}
                                            —
                                        {/if}
                                    </td>
                                    <td>
                                        {#if item.last}
                                            <span class="scan">
                                                {#if isDelivered(item)}
                                                    <button type="button" class="guide" on:click={() => deliveryId = item.shipmentId}>{ item.last.name }</button>
                                                {:else}
                                                    <span>{ item.last.name }</span>
                                                {/if}
                                                <span class="scan__time">{ item.last.created ? DateMethod.centralHourLarge(item.last.created) : '—' }</span>
                                            </span>
                                        {:else}
                                            —
                                        {/if}
                                    </td>
                                    <td>{ timeSincePreviousPoint(item) || '—' }</td>
                                    <td>{ (item.delivery && item.delivery.address) || '—' }</td>
                                </tr>
                            {/each}
                        </tbody>
                    </table>
                {:else}
                    <p class="empty">Este driver no escaneó paquetes este día.</p>
                {/if}
            {/if}
        </section>
    {/if}
    {#if deliveryId}
        <Delivery shipmentId={deliveryId} on:close={() => deliveryId = null} />
    {/if}
</View>
